import {
  Box,
  Flex,
  IconButton,
  Input,
} from "@chakra-ui/react";
import {
  forwardRef,
  KeyboardEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { SearchIcon } from "../../../components/icons/SearchIcon";
import { CloseCircleIcon } from "../../../components/icons/CloseCircleIcon";
import { useIsSmallBreakpoint } from "../../../utils/useBreakpoints";
import { useSearchV2, BROWSE_SEGMENTS } from "../hooks/useSearchContext";
import { isChannelContext } from "../types";
import { SearchContextSelector } from "./SearchContextSelector";

interface SearchBarV2Props {
  onFocus?: () => void;
  onBlur?: () => void;
  onChange?: (value: string) => void;
}

export const SearchBarV2 = forwardRef<HTMLInputElement, SearchBarV2Props>(
  function SearchBarV2({ onFocus, onBlur, onChange }, ref) {
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const {
      context, channelMeta, isOnChannelPage,
      setContext, restoreChannelContext, commitSearch, clearSearch, placeholder,
    } = useSearchV2();

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const isBrowsePage = BROWSE_SEGMENTS.has(location.pathname.split("/")[1] ?? "");
    const isExpandedSectionPage = location.pathname.startsWith("/cms/");
    const isInPlaceSearchPage = isBrowsePage || isExpandedSectionPage;
    const urlQ = searchParams.get("q") ?? "";


    // Input tracks the URL q param as source of truth when on browse page
    const [inputValue, setInputValue] = useState(urlQ);

    // Sync input from URL on in-place search pages; clear when leaving
    useEffect(() => {
      if (isInPlaceSearchPage) {
        setInputValue(urlQ);
      } else {
        setInputValue("");
      }
    }, [urlQ, isInPlaceSearchPage]);

    // Update URL q param in-place (for live search on browse page)
    const updateUrlQ = useCallback(
      (value: string) => {
        const params = new URLSearchParams(searchParams);
        if (value) {
          params.set("q", value);
        } else {
          params.delete("q");
        }
        params.sort();
        const search = params.toString();
        navigate(
          search ? `${location.pathname}?${search}` : location.pathname,
          { replace: true },
        );
      },
      [searchParams, location.pathname, navigate],
    );

    // Debounced live search — clear on pathname change to prevent stale navigations
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();
    useEffect(() => {
      return () => clearTimeout(debounceRef.current);
    }, [location.pathname]);

    const handleChange = useCallback(
      (value: string) => {
        setInputValue(value);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          if (isInPlaceSearchPage) {
            updateUrlQ(value);
          }
          onChange?.(value);
        }, 300);
      },
      [onChange, isInPlaceSearchPage, updateUrlQ],
    );

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && inputValue.trim()) {
          clearTimeout(debounceRef.current);
          if (isInPlaceSearchPage) {
            updateUrlQ(inputValue.trim());
          } else {
            commitSearch(inputValue.trim());
          }
        }
      },
      [inputValue, commitSearch, isInPlaceSearchPage, updateUrlQ],
    );

    const isSmall = useIsSmallBreakpoint();

    const handleSubmit = useCallback(() => {
      if (inputValue.trim()) {
        clearTimeout(debounceRef.current);
        if (isInPlaceSearchPage) {
          updateUrlQ(inputValue.trim());
        } else {
          commitSearch(inputValue.trim());
        }
      }
    }, [inputValue, commitSearch, isInPlaceSearchPage, updateUrlQ]);

    const handleClear = useCallback(() => {
      setInputValue("");
      clearTimeout(debounceRef.current);
      if (isInPlaceSearchPage) {
        // Remove q and browse_key, reset channel context
        const params = new URLSearchParams(searchParams);
        params.delete("q");
        params.delete("browse_key");
        params.sort();
        const search = params.toString();
        navigate(
          search ? `${location.pathname}?${search}` : location.pathname,
          { replace: true },
        );
        // Reset context selector to "all" if it was a channel
        if (isChannelContext(context)) {
          setContext("all");
        }
      } else {
        clearSearch();
      }
      onChange?.("");
    }, [clearSearch, onChange, isInPlaceSearchPage, searchParams, navigate, location.pathname, context, setContext]);

    return (
      <Flex
        w="full"
        h="46px"
        bg="transparent.white.05"
        borderRadius="16px"
        border="1px solid"
        borderColor={inputValue ? "transparent.white.30" : "transparent"}
        align="center"
        pl="4px"
        py="4px"
        overflow="hidden"
        transition="all .2s linear"
        _hover={{ bg: "transparent.white.10" }}
        _focusWithin={{ bg: "transparent.white.10", borderColor: "transparent.white.30" }}
      >
        <SearchContextSelector
          context={context}
          channelIcon={channelMeta?.icon}
          channelDisplayName={channelMeta?.displayName}
          isOnChannelPage={isOnChannelPage}
          isOnBrowsePage={isBrowsePage}
          onSelect={setContext}
          onSelectChannel={restoreChannelContext}
        />
        <Input
          ref={inputRef}
          variant="searchBar"
          h="full"
          flex={1}
          minW={0}
          pr={1}
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          data-gc-ignore-input="1"
        />
        {inputValue && (
          <>
            <CloseCircleIcon
              boxSize="24px"
              color="neutral.400"
              cursor="pointer"
              onClick={handleClear}
              aria-label="Clear search"
              flexShrink={0}
              mx="6px"
            />
            {!isSmall && (
              <Box
                w="1px"
                h="32px"
                bg="transparent.white.10"
                flexShrink={0}
              />
            )}
          </>
        )}
        <IconButton
          aria-label="Search"
          icon={<SearchIcon w="24px" h="24px" />}
          variant="ghost"
          color="dune.100"
          size="sm"
          borderRadius="full"
          minW="36px"
          h="36px"
          mx="5px"
          display={isSmall ? "none" : "flex"}
          _hover={{ bg: "transparent.white.10" }}
          onClick={handleSubmit}
        />
      </Flex>
    );
  },
);
